/**
 * 积分流水的**正负号约定** —— ✅ **全项目只有这一处**。
 *
 * ## 为什么要单独抽出来（2026-10-18 生产 bug）
 *
 * 家长端「积分 → 扣分」把一条扣分记成了 **+10**（绿色加分）。查库确认：
 *
 *     id=45769095-…  delta=+10  reason='penalty'  note='玩物丧志'
 *
 * 也就是说：**"扣分"这个 reason 配了一个正数 delta**。根因不在显示层
 * （负数流水一直按红色 `-N` 渲染，是对的），而在写入层：
 * 表单的「类型」只是给 `reason` 用的，`delta` 的符号却由家长在「分值」框里
 * **自己打负号**决定。家长点了「扣分」按钮、把预填的 `-5` 改成 `10`，
 * 服务端就老老实实存了 `+10` + `reason='penalty'` —— 一笔"加分"顶着"扣分"的名字。
 *
 * ## 约定（`reason` 决定符号，`delta` 的符号是**事实**，两者必须一致）
 *
 * | reason | 含义 | 符号 |
 * | --- | --- | --- |
 * | `checkin` | 完成打卡 | **必须为正** |
 * | `makeup` | 补卡（打折计分，但仍是加分） | **必须为正** |
 * | `bonus` | 家长额外奖励 | **必须为正** |
 * | `redeem_refund` | 兑换被拒/取消，分退回 | **必须为正** |
 * | `penalty` | 家长扣分 | **必须为负** |
 * | `redeem` | 兑换奖品扣分 | **必须为负** |
 * | `adjust` | 手动调账 / **冲正** | 两种都行（冲正 = 原流水取反） |
 *
 * ⚠️ 冲正（`reverses_ledger_id`）**一律走 `adjust`**：它的符号由被冲正那笔取反得出，
 *   所以 `adjust` 是唯一"符号不固定"的 reason。别把它收窄，否则冲正就无路可走。
 *
 * ⚠️ 这张表是"符号与 reason 是否自洽"的唯一判据：
 *   - `apps/api` 的 `applyPointsEntry()` 在写库前用它把关（最后一道闸）；
 *   - `adjustPointsSchema` 用它把家长填的分值**归一化**成正确符号；
 *   - 排查历史数据时用它跑 SQL（见 `scripts/` 里的冲正脚本）。
 */
import { POINTS_REASONS, type PointsReason } from './constants.js';

/** 某个 reason 允许的符号：`positive` / `negative` / `either`（只有 `adjust`）。 */
export type PointsSign = 'positive' | 'negative' | 'either';

/**
 * reason → 符号约定。**必须覆盖 `POINTS_REASONS` 的每一项**（用例会断言覆盖完整），
 * 这样将来加新 reason 时，类型系统会先报错提醒你把符号规则补上。
 */
export const POINTS_REASON_SIGN: Record<PointsReason, PointsSign> = {
  checkin: 'positive',
  makeup: 'positive',
  bonus: 'positive',
  redeem_refund: 'positive',
  penalty: 'negative',
  redeem: 'negative',
  adjust: 'either',
};

/** 取某个 reason 的符号约定。 */
export function pointsSignOfReason(reason: PointsReason): PointsSign {
  return POINTS_REASON_SIGN[reason];
}

/**
 * 这条流水"符号与 reason 是否自洽"。
 *
 * `adjust` 恒为 `true`（手动调账与冲正都可能朝任一方向）；
 * 其余 reason 必须严格匹配。`delta = 0` 一律不自洽 —— 数据库 `ck_points_ledger_nonzero`
 * 也不允许 0。
 */
export function pointsDeltaMatchesSign(reason: PointsReason, delta: number): boolean {
  if (!Number.isFinite(delta) || delta === 0) return false;
  const sign = pointsSignOfReason(reason);
  if (sign === 'either') return true;
  return sign === 'positive' ? delta > 0 : delta < 0;
}

/**
 * 把"家长填的分值"归一成**这个 reason 该有的符号**。
 *
 * 例如「扣分」填 `10`（或 `-10`）都得到 `-10`；「额外奖励」填 `-5`（或 `5`）都得到 `5`；
 * `adjust` 原样返回（它的方向是家长明确选的）。
 *
 * `0` 保持 `0`（调用方/数据库都会拒绝 0，这里只保证不会返回 `-0`）。
 */
export function normalizePointsDelta(reason: PointsReason, delta: number): number {
  if (!Number.isFinite(delta) || delta === 0) return 0;
  const sign = pointsSignOfReason(reason);
  if (sign === 'either') return delta;
  const magnitude = Math.abs(delta);
  return sign === 'positive' ? magnitude : -magnitude;
}

/** 给错误信息/界面用的一句话（说明这个 reason 允许什么符号）。 */
export function pointsSignRuleText(reason: PointsReason): string {
  const sign = pointsSignOfReason(reason);
  if (sign === 'either') return '可正可负（不能为 0）';
  return sign === 'positive' ? '必须为正数（加分）' : '必须为负数（扣分）';
}

/**
 * `POINTS_REASONS` 里还有没有**没被符号表覆盖**的 reason。
 *
 * 运行时兜底：`Record` 漏项只有 `tsc` 能发现，而 `apps/api` 是直接吃源码的；
 * 留一个可断言的函数，让用例把"漏项"钉成红灯而不是运行时 `undefined`。
 */
export function missingPointsSignRules(): PointsReason[] {
  return POINTS_REASONS.filter((reason) => POINTS_REASON_SIGN[reason] === undefined);
}
